import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { dateText, SharingClient, SharingResponse, SharingRow } from './api'
import styles from './GodMode.module.css'

// Co-mingling on God Mode's Tags page: a source client shares its tags with other clients, and with Share in reverse
// they share theirs back. A shared tag is no longer "unauthorised" on the dashboard, and Dataphyll Crew at a client
// downloads the legacy bins (item tags marked as bins) of every client sharing with it. Rows are added, changed and
// deleted one at a time (API /api/god-mode/tag-sharing).

interface Draft {
  id: string | null
  source: string
  targets: SharingClient[]
  reverse: boolean
}

const EMPTY: Draft = { id: null, source: '', targets: [], reverse: false }

// The clients whose names contain what is typed, leaving out the source and those already chosen.
export function matchClients(clients: SharingClient[], typed: string, exclude: string[]) {
  const q = typed.trim().toLowerCase()
  if (!q) return []
  return clients.filter((c) => !exclude.includes(c.id) && c.name.toLowerCase().includes(q)).slice(0, 8)
}

function TargetPicker({
  clients,
  chosen,
  exclude,
  onChange,
}: {
  clients: SharingClient[]
  chosen: SharingClient[]
  exclude: string[]
  onChange: (next: SharingClient[]) => void
}) {
  const [typed, setTyped] = useState('')
  const matches = matchClients(clients, typed, [...exclude, ...chosen.map((c) => c.id)])
  const add = (c: SharingClient) => {
    onChange([...chosen, c])
    setTyped('')
  }
  return (
    <div className={styles.field}>
      Sharing tags to
      <div className={styles.chips}>
        {chosen.map((c) => (
          <span key={c.id} className={styles.chip}>
            {c.name}
            <button type="button" aria-label={`Remove ${c.name}`} onClick={() => onChange(chosen.filter((x) => x.id !== c.id))}>
              ×
            </button>
          </span>
        ))}
      </div>
      <div className={styles.suggest}>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (matches[0]) add(matches[0])
            } else if (e.key === 'Backspace' && !typed && chosen.length) {
              onChange(chosen.slice(0, -1))
            }
          }}
          placeholder="Type a client's name"
          aria-label="Sharing tags to"
          autoComplete="off"
        />
        {matches.length > 0 && (
          <ul className={styles.suggestList} role="listbox" aria-label="Matching clients">
            {matches.map((c) => (
              <li key={c.id}>
                <button type="button" role="option" aria-selected={false} onClick={() => add(c)}>
                  {c.name}
                </button>
              </li>
            ))}
          </ul>
        )}
        {typed.trim() && !matches.length && <span className={styles.hint}>No other client matches.</span>}
      </div>
    </div>
  )
}

export function CoMingling() {
  const { user } = useAuth()
  const [data, setData] = useState<SharingResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await apiJson<SharingResponse>(user, '/api/god-mode/tag-sharing'))
    } catch (e: any) {
      setError(e.message)
    }
  }, [user])

  useEffect(() => {
    load()
  }, [load])

  const clients = data?.clients ?? []
  const rows = data?.rows ?? []

  const edit = (r: SharingRow) => setDraft({ id: r.id, source: r.source.id, targets: r.targets, reverse: r.reverse })

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!draft || !draft.source || !draft.targets.length) return
    setBusy(true)
    setError(null)
    try {
      const body = { source_tenant_id: draft.source, target_tenant_ids: draft.targets.map((c) => c.id), reverse: draft.reverse }
      const r = await apiJson<SharingResponse>(user, draft.id ? `/api/god-mode/tag-sharing/${draft.id}` : '/api/god-mode/tag-sharing', {
        method: draft.id ? 'PUT' : 'POST',
        body,
      })
      setData({ rows: r.rows, clients: r.clients })
      setDraft(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    setBusy(true)
    setError(null)
    try {
      const r = await apiJson<SharingResponse>(user, `/api/god-mode/tag-sharing/${id}`, { method: 'DELETE' })
      setData({ rows: r.rows, clients: r.clients })
      setDeleting(null)
      if (draft?.id === id) setDraft(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const sourceName = clients.find((c) => c.id === draft?.source)?.name
  // A row made before co-mingling was licensed keeps working; it is marked, and changing it needs the licence.
  const licensed = (id: string) => !!clients.find((c) => c.id === id)?.comingling

  return (
    <section className={`${styles.card} ${styles.stack}`} aria-label="Co-mingling">
      <h2>Co-mingling (sharing tags)</h2>
      <span className={styles.hint}>
        A client sharing its tags lets the clients it shares with use them: they are not shown as unauthorised there, and Dataphyll Crew there
        downloads its legacy bins. With Share in reverse, those clients share their tags back. Only a client licensed for co-mingling (Setup ›
        Licence and work order) can be a source; the clients it shares with need not be.
      </span>
      {error && <p className={styles.error}>{error}</p>}
      {!data && !error && <p className={styles.lead}>Loading…</p>}

      {data && rows.length === 0 && !draft && <p className={styles.empty}>No client shares its tags.</p>}
      {rows.length > 0 && (
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Source</th>
                <th>Sharing tags to</th>
                <th>Share in reverse</th>
                <th>Changed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.source.name}
                    {data && !licensed(r.source.id) && <span className={styles.hint}> · Not licensed</span>}
                  </td>
                  <td>
                    <div className={styles.chips}>
                      {r.targets.map((c) => (
                        <span key={c.id} className={styles.chip}>
                          {c.name}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{r.reverse ? 'Yes' : 'No'}</td>
                  <td>
                    {dateText(r.updated_at)}
                    {r.updated_by ? ` · ${r.updated_by}` : ''}
                  </td>
                  <td>
                    <div className={styles.inline}>
                      <button type="button" className={styles.secondary} onClick={() => edit(r)} disabled={busy} aria-label={`Edit ${r.source.name}'s sharing`}>
                        Edit
                      </button>
                      {deleting === r.id ? (
                        <>
                          <button type="button" className={styles.primary} onClick={() => remove(r.id)} disabled={busy}>
                            Delete
                          </button>
                          <button type="button" className={styles.secondary} onClick={() => setDeleting(null)} disabled={busy}>
                            Keep
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={styles.secondary}
                          onClick={() => setDeleting(r.id)}
                          disabled={busy}
                          aria-label={`Delete ${r.source.name}'s sharing`}
                        >
                          Delete…
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft ? (
        <form className={styles.form} onSubmit={save} aria-label={draft.id ? 'Change sharing' : 'Add sharing'}>
          <h3>{draft.id ? 'Change sharing' : 'Add sharing'}</h3>
          <label className={styles.field}>
            Source client
            <select
              value={draft.source}
              onChange={(e) => setDraft({ ...draft, source: e.target.value, targets: draft.targets.filter((c) => c.id !== e.target.value) })}
              aria-label="Source client"
            >
              <option value="">Choose a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id} disabled={!c.comingling && c.id !== draft.source}>
                  {c.name}
                  {c.comingling ? '' : ' (not licensed)'}
                </option>
              ))}
            </select>
          </label>
          <TargetPicker clients={clients} chosen={draft.targets} exclude={draft.source ? [draft.source] : []} onChange={(t) => setDraft({ ...draft, targets: t })} />
          <label>
            <input type="checkbox" checked={draft.reverse} onChange={(e) => setDraft({ ...draft, reverse: e.target.checked })} /> Share in reverse
            {draft.targets.length > 0 && sourceName ? ` (${draft.targets.map((c) => c.name).join(', ')} share their tags with ${sourceName} too)` : ''}
          </label>
          <div className={styles.inline}>
            <button type="submit" className={styles.primary} disabled={busy || !draft.source || !draft.targets.length}>
              {draft.id ? 'Save changes' : 'Add'}
            </button>
            <button type="button" className={styles.secondary} onClick={() => setDraft(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        data && (
          <div className={styles.inline}>
            <button type="button" className={styles.secondary} onClick={() => setDraft(EMPTY)}>
              Add sharing
            </button>
          </div>
        )
      )}
    </section>
  )
}
